import type {
  AuthenticationResponseJSON,
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
  RegistrationResponseJSON,
} from '@simplewebauthn/browser'

import { API_BASE_URL } from './baseUrl'

// Typed client for the backend's /api/auth endpoints (see
// backend/docs/auth/api.md). The error codes below are a public contract with
// the backend - it adds codes but never renames them.

export type AuthApiErrorCode =
  | 'AUTH_INVALID_REQUEST'
  | 'AUTH_OTP_INVALID'
  | 'AUTH_OTP_LOCKED'
  | 'AUTH_ACCOUNT_DISABLED'
  | 'AUTH_RATE_LIMITED'
  | 'AUTH_METHOD_DISABLED'
  | 'AUTH_EMAIL_UNAVAILABLE'
  | 'AUTH_INVALID_TOKEN'
  | 'AUTH_REAUTH_REQUIRED'
  | 'AUTH_PASSKEY_INVALID'
  | 'AUTH_PASSKEY_LIMIT'
  | 'AUTH_PASSKEY_EXISTS'
  | 'AUTH_PASSKEY_NOT_FOUND'
  // Client-side only: the request never got a usable answer.
  | 'AUTH_NETWORK'
  | 'AUTH_UNKNOWN'

const KNOWN_SERVER_CODES: ReadonlySet<string> = new Set<AuthApiErrorCode>([
  'AUTH_INVALID_REQUEST',
  'AUTH_OTP_INVALID',
  'AUTH_OTP_LOCKED',
  'AUTH_ACCOUNT_DISABLED',
  'AUTH_RATE_LIMITED',
  'AUTH_METHOD_DISABLED',
  'AUTH_EMAIL_UNAVAILABLE',
  'AUTH_INVALID_TOKEN',
  'AUTH_REAUTH_REQUIRED',
  'AUTH_PASSKEY_INVALID',
  'AUTH_PASSKEY_LIMIT',
  'AUTH_PASSKEY_EXISTS',
  'AUTH_PASSKEY_NOT_FOUND',
])

const REQUEST_TIMEOUT_MS = 15_000

export class AuthApiError extends Error {
  readonly code: AuthApiErrorCode
  readonly status?: number
  /** Seconds to wait before retrying, from the `Retry-After` header (rate limits). */
  readonly retryAfterSec?: number

  constructor({
    code,
    status,
    retryAfterSec,
    message,
  }: {
    code: AuthApiErrorCode
    status?: number
    retryAfterSec?: number
    message?: string
  }) {
    super(message ?? code)
    this.name = 'AuthApiError'
    this.code = code
    this.status = status
    this.retryAfterSec = retryAfterSec
  }
}

export interface AuthMethods {
  emailOtp: boolean
  passkey: boolean
}

export interface StartEmailCodeResult {
  expiresInSec: number
  resendAfterSec: number
}

const request = async <T>(
  path: string,
  init: { method: 'GET' | 'POST' | 'DELETE'; body?: unknown; bearerToken?: string }
): Promise<T> => {
  let response: Response
  try {
    response = await fetch(`${API_BASE_URL}/api/auth${path}`, {
      method: init.method,
      headers: {
        'Content-Type': 'application/json',
        ...(init.bearerToken && { Authorization: `Bearer ${init.bearerToken}` }),
      },
      body: init.body === undefined ? undefined : JSON.stringify(init.body),
      signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
    })
  } catch {
    // Offline, DNS failure, timeout, etc.
    throw new AuthApiError({ code: 'AUTH_NETWORK' })
  }

  const payload = await response.json().catch(() => null)

  if (!response.ok) {
    const serverCode = (payload as { code?: string } | null)?.code
    const retryAfter = Number(response.headers.get('Retry-After'))
    throw new AuthApiError({
      code: serverCode && KNOWN_SERVER_CODES.has(serverCode) ? (serverCode as AuthApiErrorCode) : 'AUTH_UNKNOWN',
      status: response.status,
      retryAfterSec: Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : undefined,
    })
  }

  if (payload === null) {
    throw new AuthApiError({ code: 'AUTH_UNKNOWN', status: response.status })
  }
  return payload as T
}

/**
 * Which sign-in methods the backend currently offers (a server-side kill
 * switch, so a method can be turned off without an app release). Fails
 * closed: if the backend can't be reached, no server-verified method is shown.
 */
export const getAuthMethods = async (): Promise<AuthMethods> => {
  try {
    const { methods } = await request<{ methods: Partial<AuthMethods> }>('/methods', { method: 'GET' })
    return { emailOtp: methods?.emailOtp === true, passkey: methods?.passkey === true }
  } catch {
    return { emailOtp: false, passkey: false }
  }
}

/** Emails a 6-digit sign-in code. The backend responds identically for known and unknown addresses. */
export const startEmailCode = (email: string): Promise<StartEmailCodeResult> =>
  request<StartEmailCodeResult>('/email/start', { method: 'POST', body: { email } })

/** Exchanges the emailed code for a Firebase custom token. */
export const verifyEmailCode = async (email: string, code: string): Promise<string> => {
  const { customToken } = await request<{ customToken?: string }>('/email/verify', {
    method: 'POST',
    body: { email, code },
  })
  if (!customToken) {
    throw new AuthApiError({ code: 'AUTH_UNKNOWN' })
  }
  return customToken
}

/**
 * Permanently deletes the signed-in player's account. `idToken` is the Firebase
 * ID token of the current user. Rejects with AUTH_REAUTH_REQUIRED if their last
 * sign-in was more than a few minutes ago.
 */
export const deleteAccountRequest = async (idToken: string): Promise<void> => {
  await request<{ deleted: boolean }>('/account', { method: 'DELETE', bearerToken: idToken })
}

// ---- Passkeys (WebAuthn) ----------------------------------------------------
// Design: backend/docs/auth/adr-004-passkeys.md. The WebAuthn JSON goes to and
// from @simplewebauthn/browser unchanged; this file only moves it over HTTP.

/** What the player sees about one of their passkeys. Dates are ISO strings, as sent. */
export interface PasskeySummary {
  id: string
  nickname: string | null
  createdAt: string
  lastUsedAt: string | null
  /** Synced across the player's devices (iCloud Keychain, Google Password Manager) rather than tied to one. */
  synced: boolean
}

/** Options for a usernameless sign-in: no identifier, the device offers the passkeys it holds for this site. */
export const getPasskeyLoginOptions = async (): Promise<PublicKeyCredentialRequestOptionsJSON> => {
  const { options } = await request<{ options?: PublicKeyCredentialRequestOptionsJSON }>('/passkey/login/options', {
    method: 'POST',
  })
  if (!options) throw new AuthApiError({ code: 'AUTH_UNKNOWN' })
  return options
}

/** Exchanges a signed passkey assertion for a Firebase custom token. */
export const verifyPasskeyLogin = async (response: AuthenticationResponseJSON): Promise<string> => {
  const { customToken } = await request<{ customToken?: string }>('/passkey/login/verify', {
    method: 'POST',
    body: { response },
  })
  if (!customToken) throw new AuthApiError({ code: 'AUTH_UNKNOWN' })
  return customToken
}

/** Needs a Firebase ID token from a recent sign-in (else AUTH_REAUTH_REQUIRED). */
export const getPasskeyRegistrationOptions = async (idToken: string): Promise<PublicKeyCredentialCreationOptionsJSON> => {
  const { options } = await request<{ options?: PublicKeyCredentialCreationOptionsJSON }>('/passkey/register/options', {
    method: 'POST',
    bearerToken: idToken,
  })
  if (!options) throw new AuthApiError({ code: 'AUTH_UNKNOWN' })
  return options
}

export const verifyPasskeyRegistration = async (
  idToken: string,
  response: RegistrationResponseJSON,
  nickname?: string
): Promise<PasskeySummary> => {
  const { passkey } = await request<{ passkey?: PasskeySummary }>('/passkey/register/verify', {
    method: 'POST',
    body: { response, ...(nickname && { nickname }) },
    bearerToken: idToken,
  })
  if (!passkey) throw new AuthApiError({ code: 'AUTH_UNKNOWN' })
  return passkey
}

export const listPasskeysRequest = async (idToken: string): Promise<PasskeySummary[]> => {
  const { passkeys } = await request<{ passkeys?: PasskeySummary[] }>('/passkeys', {
    method: 'GET',
    bearerToken: idToken,
  })
  if (!Array.isArray(passkeys)) throw new AuthApiError({ code: 'AUTH_UNKNOWN' })
  return passkeys
}

/** Needs a recent sign-in (else AUTH_REAUTH_REQUIRED). */
export const removePasskeyRequest = async (idToken: string, id: string): Promise<void> => {
  await request<{ removed: boolean }>(`/passkeys/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    bearerToken: idToken,
  })
}
